import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    UseQueryOptions,
    UseMutationOptions,
    useQueryClient,
} from 'react-query';

import {
    MutationMode,
    RaRecord,
    RedirectionSideEffect,
    SaveContextValue,
    TransformData,
    useAuthenticated,
    useGetOne,
    UseGetOneHookValue,
    useGetRecordRepresentation,
    useGetResourceLabel,
    useMutationMiddlewares,
    useNotify,
    useRedirect,
    useRefresh,
    useResourceContext,
    useTranslate,
    useUpdate,
    UseUpdateMutateParams,
} from 'react-admin';

/**
 * Prepare data for the Edit view.
 *
 * useEditNodeController does a few things:
 * - it grabs the id from the URL and the resource name from the ResourceContext,
 * - it fetches the record via useGetOne,
 * - it prepares the page title.
 *
 * @param {Object} props The props passed to the EditNode component.
 *
 * @return {Object} controllerProps Fetched data and callbacks for the Edit view
 *
 * @example
 *
 * import { useEditNodeController } from '@react-admin/ra-tree';
 * import EditView from './EditView';
 *
 * const MyEdit = () => {
 *     const controllerProps = useEditNodeController({ resource: 'posts', id: 123 });
 *     return <EditView {...controllerProps} {...props} />;
 * }
 */
export const useEditNodeController = <
    RecordType extends RaRecord = any,
    MutationOptionsError = unknown
>(
    props: EditControllerProps<RecordType, MutationOptionsError> = {}
): EditControllerResult<RecordType> => {
    const {
        disableAuthentication,
        id: propsId,
        mutationMode = 'undoable',
        mutationOptions = {},
        queryOptions = {},
        redirect: redirectTo = DefaultRedirect,
        transform,
    } = props;
    useAuthenticated({ enabled: !disableAuthentication });
    const resource = useResourceContext(props);
    const getRecordRepresentation = useGetRecordRepresentation(resource);
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const queryClient = useQueryClient();

    const { id: routeId } = useParams<'id'>();
    const id = propsId || decodeURIComponent(routeId);
    const { meta: queryMeta, ...otherQueryOptions } = queryOptions;
    const {
        onSuccess,
        onError,
        meta: mutationMeta,
        ...otherMutationOptions
    } = mutationOptions;
    const {
        registerMutationMiddleware,
        getMutateWithMiddlewares,
        unregisterMutationMiddleware,
    } = useMutationMiddlewares();
    const {
        data: record,
        error,
        isLoading,
        isFetching,
        refetch,
    } = useGetOne<RecordType>(
        resource,
        { id, meta: queryMeta },
        {
            onError: () => {
                notify('ra.notification.item_doesnt_exist', {
                    type: 'warning',
                });
                redirect('list', resource);
                refresh();
            },
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retry: false,
            ...otherQueryOptions,
        }
    );

    // eslint-disable-next-line eqeqeq
    if (record && record.id && record.id != id) {
        throw new Error(
            `useEditNodeController: Fetched record's id attribute (${record.id}) must match the requested 'id' (${id})`
        );
    }

    const getResourceLabel = useGetResourceLabel();
    const recordRepresentation = getRecordRepresentation(record);
    const defaultTitle = translate('ra.page.edit', {
        name: getResourceLabel(resource, 1),
        id,
        record,
        recordRepresentation:
            typeof recordRepresentation === 'string'
                ? recordRepresentation
                : '',
    });

    const recordCached = { id, previousData: record };

    const [update, { isLoading: saving }] = useUpdate<
        RecordType,
        MutationOptionsError
    >(resource, recordCached, { ...otherMutationOptions, mutationMode });

    const save = useCallback(
        (
            data: Partial<RecordType>,
            {
                onSuccess: onSuccessFromSave,
                onError: onErrorFromSave,
                transform: transformFromSave,
            }: any = {}
        ) =>
            Promise.resolve(
                transformFromSave
                    ? transformFromSave(data, {
                          previousData: recordCached.previousData,
                      })
                    : transform
                    ? transform(data, {
                          previousData: recordCached.previousData,
                      })
                    : data
            ).then((data: Partial<RecordType>) => {
                const mutate = getMutateWithMiddlewares(update);
                return mutate(
                    resource,
                    { id, data, meta: mutationMeta },
                    {
                        onSuccess: async (newData, variables, context) => {
                            // TODO: remove when react-admin v4.1.3 is released
                            const updatedData = { ...newData, ...data };

                            // hack: only way to tell react-query not to fetch this query for the next 5 seconds
                            // because setQueryData doesn't accept a stale time option
                            const now = Date.now();
                            const updatedAt =
                                mutationMode === 'undoable'
                                    ? now + 5 * 1000
                                    : now;

                            const updateColl = (old: RecordType[]) => {
                                if (!old) return;
                                const index = old.findIndex(
                                    // eslint-disable-next-line eqeqeq
                                    record => record.id == id
                                );
                                if (index === -1) {
                                    return old;
                                }
                                const result = [
                                    ...old.slice(0, index),
                                    //{ ...old[index], ...updatedData },
                                    { ...old[index], ...updatedData, parent: old[index].parent ?? {id:0}, children: old[index].children ?? [] },
                                    ...old.slice(index + 1),
                                ];

                                return result;
                            };

                            type GetListResult = {
                                data?: RecordType[];
                                total?: number;
                            };

                            queryClient.setQueryData(
                                [resource, 'getOne', { id: String(id) }],
                                (record: RecordType) => ({
                                    ...record,
                                    ...updatedData,
                                }),
                                { updatedAt }
                            );
                            queryClient.setQueriesData(
                                [resource, 'getList'],
                                (res: GetListResult) =>
                                    res && res.data
                                        ? {
                                              data: updateColl(res.data),
                                              total: res.total,
                                          }
                                        : res,
                                { updatedAt }
                            );
                            queryClient.setQueriesData(
                                [resource, 'getTree'],
                                (coll: GetListResult['data']) =>
                                    coll && coll.length
                                        ? updateColl(coll)
                                        : coll,
                                { updatedAt }
                            );
                            queryClient.setQueriesData(
                                [resource, 'getRootNodes'],
                                (coll: GetListResult['data']) =>
                                    coll && coll.length
                                        ? updateColl(coll)
                                        : coll,
                                { updatedAt }
                            );
                            queryClient.setQueriesData(
                                [resource, 'getMany'],
                                (coll: RecordType[]) =>
                                    coll && coll.length > 0
                                        ? updateColl(coll)
                                        : coll,
                                { updatedAt }
                            );
                            queryClient.setQueriesData(
                                [resource, 'getManyReference'],
                                (res: GetListResult) =>
                                    res && res.data
                                        ? {
                                              data: updateColl(res.data),
                                              total: res.total,
                                          }
                                        : res,
                                { updatedAt }
                            );

                            if (onSuccessFromSave) {
                                return onSuccessFromSave(
                                    newData,
                                    variables,
                                    context
                                );
                            }

                            if (onSuccess) {
                                return onSuccess(newData, variables, context);
                            }

                            notify('ra.notification.updated', {
                                type: 'info',
                                messageArgs: { smart_count: 1 },
                                undoable: mutationMode === 'undoable',
                            });
                            redirect(redirectTo, resource, newData.id, newData);
                        },
                        onError: onErrorFromSave
                            ? onErrorFromSave
                            : onError
                            ? onError
                            : (error: Error | string) => {
                                  notify(
                                      typeof error === 'string'
                                          ? error
                                          : error.message ||
                                                'ra.notification.http_error',
                                      {
                                          type: 'warning',
                                          messageArgs: {
                                              _:
                                                  typeof error === 'string'
                                                      ? error
                                                      : error && error.message
                                                      ? error.message
                                                      : undefined,
                                          },
                                      }
                                  );
                              },
                    }
                );
            }),
        [
            id,
            getMutateWithMiddlewares,
            mutationMode,
            mutationMeta,
            notify,
            onError,
            onSuccess,
            queryClient,
            recordCached.previousData,
            redirect,
            redirectTo,
            resource,
            transform,
            update,
        ]
    );

    return {
        defaultTitle,
        error,
        isFetching,
        isLoading,
        mutationMode,
        record,
        redirect: DefaultRedirect,
        refetch,
        registerMutationMiddleware,
        resource,
        save,
        saving,
        unregisterMutationMiddleware,
    };
};

export interface EditControllerProps<
    RecordType extends RaRecord = any,
    MutationOptionsError = unknown
> {
    disableAuthentication?: boolean;
    id?: RecordType['id'];
    mutationMode?: MutationMode;
    mutationOptions?: UseMutationOptions<
        RecordType,
        MutationOptionsError,
        UseUpdateMutateParams<RecordType>
    >;
    queryOptions?: UseQueryOptions<RecordType> & { meta?: any };
    redirect?: RedirectionSideEffect;
    resource?: string;
    transform?: TransformData;
    [key: string]: any;
}

export interface EditControllerResult<RecordType extends RaRecord = any>
    extends SaveContextValue {
    // Necessary for actions (EditActions) which expect a data prop containing the record
    // @deprecated - to be removed in 4.0d
    data?: RecordType;
    error?: any;
    defaultTitle: string;
    isFetching: boolean;
    isLoading: boolean;
    record?: RecordType;
    refetch: UseGetOneHookValue<RecordType>['refetch'];
    redirect: RedirectionSideEffect;
    resource: string;
}

const DefaultRedirect = 'list';
